import IconeAjuda from '@/global/components/IconeAjuda'
import Grid from '@/global/components/ui/Grid'
import { schema } from '@acme/core'
import dayjs from 'dayjs'
import { FormDatePicker } from 'global/components/Form'
import { SelectFuncionario } from 'global/components/Selects'
import Dialog from 'global/guides/Dialog'
import { useMutation } from 'react-query'
import { escalas } from 'service/api/endpoints/escalas'
import { toast } from 'sonner'
import { useStore } from 'store'
import { z } from 'zod'

const escalaFuncionarioSchema = schema.EscalasTrabalhoFuncionarioSchema.partial(
    {
        dtFinal: true,
    },
)
    .pick({ dtInicial: true, dtFinal: true })
    .extend({ funcionarioId: z.number().int() })

type EscalaFuncionario = z.infer<typeof escalaFuncionarioSchema>

interface Props {
    escalaId: string
    open: boolean
    onClose: () => void
    /** Recarrega a lista depois de associar. */
    onAssociado: () => void
}

/** Associa um funcionário à escala a partir de uma data, com fim opcional. */
export function AdicionarFuncionarioEscala({
    escalaId,
    open,
    onClose,
    onAssociado,
}: Props) {
    const funcionarioId = useStore(state => state.employeeId)
    const { mutateAsync, isLoading } = useMutation(
        ['escalas', escalaId, 'funcionarios'],
        (data: EscalaFuncionario) =>
            escalas.postFrom<EscalaFuncionario>(escalaId, 'funcionarios', data),
    )

    async function handleSubmit(
        data: Omit<EscalaFuncionario, 'funcionarioId'>,
    ) {
        const validation = await escalaFuncionarioSchema.safeParseAsync({
            ...data,
            funcionarioId,
        })

        if (!validation.success) {
            toast.error('Erro de validação, verifique os campos')
            throw validation.error
        }

        try {
            await mutateAsync(validation.data)
            toast.success('Funcionário associado com sucesso')
            onAssociado()
        } catch (error: any) {
            toast.error(
                error.statusCode === 400
                    ? error.message
                    : 'Erro ao associar funcionário',
            )
            throw error
        }
    }

    return (
        <Dialog.Root
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            loading={isLoading}
            defaultValues={{ dtInicial: dayjs().format('YYYY-MM-DD') }}
        >
            <Dialog.Title>
                <span className="flex items-center">
                    Associar funcionário
                    <IconeAjuda>
                        <li>
                            Caso a data final seja informada, o funcionário será
                            desassociado da escala na data informada.
                        </li>
                        <br />
                        <li>
                            Com data final em branco, o funcionário fica na
                            escala até ser desassociado manualmente.
                        </li>
                    </IconeAjuda>
                </span>
            </Dialog.Title>
            <Dialog.Content>
                <Grid container columnSpacing={2}>
                    <Grid item xs={12}>
                        <SelectFuncionario variant="form" />
                    </Grid>
                    <Grid item xs={6}>
                        <FormDatePicker
                            name="dtInicial"
                            label="Data inicial"
                            type="date"
                            required
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormDatePicker
                            name="dtFinal"
                            label="Data final"
                            type="date"
                            helperText="Deixe em branco para indeterminada"
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
